import { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '@/lib/api-client';
import { handleNavigation, handleFormOpen, buildConfirmationData } from '@/lib/ai-tools';
import type { ConfirmationData } from '@/lib/ai-tools';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system' | 'briefing';
  content: string;
  timestamp: string;
  toolResults?: Array<{ tool: string; result: unknown }>;
  confirmation?: ConfirmationData & { status: 'pending' | 'confirmed' | 'cancelled' };
  usage?: { messages_today: number; remaining: number; cost_remaining: number };
}

// Shape returned by POST /api/ai/chat. Side-effect fields are arrays because
// a single chat turn can fan out into multiple tool calls. The fields are
// optional — present only when at least one tool of that kind was invoked.
interface ChatResponse {
  message: string;
  tool_results?: Array<{ tool: string; result: unknown }>;
  confirmations?: Array<{
    action: string;
    details: Record<string, unknown>;
  }>;
  navigations?: Array<{
    page: string;
    params?: Record<string, string>;
  }>;
  form_opens?: Array<{
    form: string;
    prefill?: Record<string, unknown>;
  }>;
  conversation_id: string;
  usage: {
    messages_today: number;
    remaining: number;
    cost_remaining: number;
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

// Simulates streaming: sets content word-by-word with a small delay
async function typewriterEffect(
  fullText: string,
  onUpdate: (partial: string) => void
): Promise<void> {
  const words = fullText.split(' ');
  let displayed = '';
  for (let i = 0; i < words.length; i++) {
    displayed += (i === 0 ? '' : ' ') + words[i];
    onUpdate(displayed);
    await new Promise<void>((resolve) => setTimeout(resolve, 28));
  }
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useAIChat() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [usage, setUsage] = useState<{ messages_today: number; remaining: number; cost_remaining: number } | null>(null);
  const isTypingRef = useRef(false);

  // ── Send a message ─────────────────────────────────────────────────────────
  const sendMessage = useCallback(
    async (content: string, additionalContext?: string) => {
      if (!content.trim() || isLoading) return;

      setError(null);

      const userMsg: ChatMessage = {
        id: generateId(),
        role: 'user',
        content: content.trim(),
        timestamp: new Date().toISOString(),
      };

      setMessages((prev) => [...prev, userMsg]);
      setIsLoading(true);

      // Build conversation history for backend (last 10 turns)
      const history = messages
        .filter((m) => m.role === 'user' || m.role === 'assistant')
        .slice(-10)
        .map((m) => ({ role: m.role as 'user' | 'assistant', content: m.content }));

      try {
        const data = await apiRequest<ChatResponse>('/api/ai/chat', {
          method: 'POST',
          body: JSON.stringify({
            message: content.trim(),
            conversation_id: conversationId ?? undefined,
            conversation_history: history,
            ...(additionalContext ? { additional_context: additionalContext } : {}),
          }),
        });

        if (data.conversation_id) setConversationId(data.conversation_id);
        if (data.usage) setUsage(data.usage);

        // Side-effect arrays — server emits one entry per tool invocation.
        if (data.navigations?.length) {
          for (const nav of data.navigations) handleNavigation(nav, navigate);
        }
        if (data.form_opens?.length) {
          for (const form of data.form_opens) handleFormOpen(form);
        }

        // Confirmations — render one card per write tool invocation. The
        // server returns semantic { action, details }; we map each to the
        // executable ConfirmationData via buildConfirmationData. Unknown
        // actions are dropped with a console warning rather than rendering
        // a broken card the user can't dismiss.
        if (data.confirmations?.length) {
          const confirmationMessages: ChatMessage[] = [];
          let textShown = false;
          for (const raw of data.confirmations) {
            const built = buildConfirmationData(raw);
            if (!built) {
              console.warn('AI returned unknown confirmation action:', raw.action);
              continue;
            }
            confirmationMessages.push({
              id: generateId(),
              role: 'assistant',
              // Show the assistant's lead-in text on the first card only;
              // subsequent cards have no body so they read as a stack of
              // discrete actions to confirm.
              content: textShown ? '' : data.message,
              timestamp: new Date().toISOString(),
              usage: textShown ? undefined : data.usage,
              confirmation: { ...built, status: 'pending' },
            });
            textShown = true;
          }
          if (confirmationMessages.length > 0) {
            setMessages((prev) => [...prev, ...confirmationMessages]);
            setIsLoading(false);
            return;
          }
          // All confirmations were unknown — fall through to render the
          // assistant's text-only message so the user isn't left hanging.
        }

        // Build assistant message (with optional tool results)
        const assistantId = generateId();
        const assistantMsg: ChatMessage = {
          id: assistantId,
          role: 'assistant',
          content: '',
          timestamp: new Date().toISOString(),
          toolResults: data.tool_results ?? [],
          usage: data.usage,
        };

        setMessages((prev) => [...prev, assistantMsg]);
        isTypingRef.current = true;

        // Typewriter for the text portion
        await typewriterEffect(data.message, (partial) => {
          setMessages((prev) =>
            prev.map((m) => (m.id === assistantId ? { ...m, content: partial } : m))
          );
        });

        isTypingRef.current = false;
      } catch (err) {
        const e = err instanceof Error ? err : new Error('AI request failed');
        setError(e);

        const errorMsg: ChatMessage = {
          id: generateId(),
          role: 'assistant',
          content: `Sorry, I ran into an error: ${e.message}. Please try again.`,
          timestamp: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, errorMsg]);
      } finally {
        setIsLoading(false);
      }
    },
    [messages, isLoading, conversationId, navigate]
  );

  // ── Resolve a confirmation card ────────────────────────────────────────────
  const resolveConfirmation = useCallback(
    (messageId: string, status: 'confirmed' | 'cancelled', resultMessage: string) => {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId && m.confirmation
            ? { ...m, confirmation: { ...m.confirmation, status } }
            : m
        )
      );

      const resultMsg: ChatMessage = {
        id: generateId(),
        role: 'assistant',
        content: resultMessage,
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, resultMsg]);
    },
    []
  );

  // ── Load most recent conversation from backend ─────────────────────────────
  const loadRecentConversation = useCallback(async () => {
    if (messages.length > 0 || conversationId) return; // already have messages
    try {
      const data = await apiRequest<{ conversations: Array<{ id: string; messages: Array<{ role: string; content: string }>; tool_type: string }> }>(
        '/api/ai/conversations?limit=1'
      );
      const recent = data.conversations?.[0];
      if (recent?.messages?.length > 0) {
        setConversationId(recent.id);
        const loaded: ChatMessage[] = recent.messages.map((m, i) => ({
          id: `loaded-${i}-${Date.now()}`,
          role: m.role as ChatMessage['role'],
          content: m.content,
          timestamp: new Date().toISOString(),
        }));
        setMessages(loaded);
      }
    } catch {
      // Silent — not critical if we can't load history
    }
  }, [messages.length, conversationId]);

  // ── Clear conversation ─────────────────────────────────────────────────────
  const clearConversation = useCallback(() => {
    setMessages([]);
    setConversationId(null);
    setError(null);
  }, []);

  return {
    messages,
    isLoading,
    error,
    conversationId,
    usage,
    sendMessage,
    loadRecentConversation,
    clearConversation,
    resolveConfirmation,
  };
}
